/* @jsx h */
import { defineComponent, h, onMounted, ref, computed } from "vue";
import { Promotion } from "@lemmonhq/types";
import { ResponsiveTable, useLocalization } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { getRequirementText, getRewardText } from "@lemmonhq/orders";
import { NewPromotionEditor as PromotionEditor } from "./NewPromotionEditor";
import { getScheduleText } from "./schedule-text";
import { api } from "@app/data/api";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard(props: {
  icon: string;
  label: string;
  value: string | number;
  iconBg: string;
  iconColor: string;
  valueColor: string;
}) {
  return (
    <div class="grid grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-2 bg-base-100 rounded-xl border px-2 py-3 text-left l-box-shadow sm:grid-cols-[2.75rem_minmax(0,1fr)] sm:gap-4 sm:px-5 sm:py-4">
      <div
        class={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl ${props.iconBg} flex items-center justify-center shrink-0`}
      >
        <span class={`material-symbols-outlined text-xl ${props.iconColor}`}>
          {props.icon}
        </span>
      </div>
      <div class="min-w-0">
        <p class="break-words text-[10px] leading-tight text-base-content/40 uppercase tracking-wide font-medium sm:text-xs sm:tracking-wider">
          {props.label}
        </p>
        <p class={`text-2xl font-bold leading-tight ${props.valueColor}`}>
          {props.value}
        </p>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const LoyaltyPromotionsPage = defineComponent({
  name: "LoyaltyPromotionsPage",
  setup() {
    const { t } = useLocalization();
    const promotions = ref<Promotion[]>([]);
    const loading = ref<boolean | Error>(true);
    const showEditor = ref(false);
    const editingPromotion = ref<Promotion | null>(null);
    const searchQuery = ref("");
    const filterStatus = ref<"all" | "active" | "inactive">("all");
    const togglingId = ref<string | null>(null);

    const loadPromotions = async () => {
      try {
        loading.value = true;
        promotions.value = await api.getPromotions();
        loading.value = false;
      } catch (error: any) {
        loading.value = new Error(error.message || "Failed to load promotions");
      }
    };

    onMounted(loadPromotions);

    const filtered = computed(() => {
      let list = promotions.value;
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter((p) => p.name.toLowerCase().includes(q));
      }
      if (filterStatus.value !== "all") {
        list = list.filter((p) =>
          filterStatus.value === "active" ? p.enabled : !p.enabled,
        );
      }
      return list;
    });

    const handleToggle = async (promotion: Promotion) => {
      try {
        togglingId.value = promotion.id;
        const updated = await api.updatePromotion({
          id: promotion.id,
          enabled: !promotion.enabled,
        });
        const idx = promotions.value.findIndex((p) => p.id === promotion.id);
        if (idx !== -1) promotions.value[idx] = updated;
      } catch (error: any) {
        alert("Failed to update promotion: " + error.message);
      } finally {
        togglingId.value = null;
      }
    };

    const handleSave = (promotion: Promotion) => {
      if (editingPromotion.value) {
        const idx = promotions.value.findIndex(
          (p) => p.id === editingPromotion.value!.id,
        );
        if (idx !== -1) promotions.value[idx] = promotion;
      } else {
        promotions.value.unshift(promotion);
      }
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "name",
        label: "Promotion",
        icon: "local_offer",
        mobilePriority: "primary",
        render: (row: Promotion) => (
          <div class="flex flex-col min-w-0">
            <span class="font-medium text-accent truncate">{row.name}</span>
            <span
              class={`inline-flex items-center gap-1 mt-0.5 text-xs font-semibold uppercase w-fit px-1.5 py-0.5 rounded ${
                row.enabled
                  ? "bg-success/15 text-success"
                  : "bg-base-content/10 text-base-content/40"
              }`}
            >
              <span class="material-symbols-outlined text-xs">
                {row.enabled ? "check_circle" : "cancel"}
              </span>
              {row.enabled ? "Active" : "Inactive"}
            </span>
          </div>
        ),
      },
      {
        key: "requirement",
        label: "Requirement",
        icon: "task_alt",
        mobilePriority: "secondary",
        render: (row: Promotion) => {
          const text =
            row.requirements.map((r) => getRequirementText(r)).join(", ") || "—";
          return (
            <span
              class="block max-w-[18rem] truncate text-sm text-base-content/70"
              title={text}
            >
              {text}
            </span>
          );
        },
      },
      {
        key: "reward",
        label: "Reward",
        icon: "redeem",
        mobilePriority: "secondary",
        render: (row: Promotion) => {
          const text = getRewardText(row.reward);
          return (
            <span
              class="block max-w-[18rem] truncate text-sm text-base-content/70"
              title={text}
            >
              {text}
            </span>
          );
        },
      },
      {
        key: "schedule",
        label: "Schedule",
        icon: "schedule",
        mobilePriority: "secondary",
        render: (row: Promotion) => {
          const schedule = getScheduleText(row.days);
          if (!schedule) {
            return <span class="text-sm text-base-content/30">Any time</span>;
          }
          return (
            <span
              class="flex max-w-[16rem] min-w-0 items-baseline gap-1.5 text-sm text-base-content/60"
              title={schedule.title}
            >
              <span class="truncate">{schedule.days}</span>
              {schedule.time && (
                <span class="shrink-0 text-xs text-base-content/40 tabular-nums">
                  {schedule.time}
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "actions",
        render: (row: Promotion) => (
          <div
            class="flex items-center justify-end gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              disabled={togglingId.value === row.id}
              onClick={() => handleToggle(row)}
              class={`btn btn-xs btn-ghost ${row.enabled ? "text-warning hover:bg-warning/10" : "text-success hover:bg-success/10"}`}
              title={row.enabled ? "Disable" : "Enable"}
            >
              {togglingId.value === row.id ? (
                <span class="loading loading-spinner loading-xs" />
              ) : (
                <span class="material-symbols-outlined text-base">
                  {row.enabled ? "stop_circle" : "play_circle"}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                editingPromotion.value = row;
                showEditor.value = true;
              }}
              class="btn btn-xs btn-ghost hover:bg-base-content/10"
              title="Edit"
            >
              <span class="material-symbols-outlined text-base">edit</span>
            </button>
          </div>
        ),
      },
    ];

    return () => (
      <div class="flex min-w-0 flex-1 flex-col">
        <AppSubpageHeader
          icon="local_offer"
          title="Promotions"
          description={t("promotions.page.description")}
        >
          {{
            actions: () => (
              <button
                class="btn btn-primary btn-sm gap-2 shadow-lg hover:shadow-primary/25"
                onClick={() => {
                  editingPromotion.value = null;
                  showEditor.value = true;
                }}
              >
                <span class="material-symbols-outlined text-lg">add</span>
                New Promotion
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent>
            {loading.value instanceof Error ? (
              <div class="flex items-center justify-center h-64">
                <div class="text-center space-y-3">
                  <span class="material-symbols-outlined text-5xl text-error/60">
                    error_outline
                  </span>
                  <p class="text-sm text-base-content/60">
                    {loading.value.message}
                  </p>
                  <button
                    class="btn btn-sm btn-outline btn-error gap-2"
                    onClick={loadPromotions}
                  >
                    <span class="material-symbols-outlined text-base">
                      refresh
                    </span>
                    Retry
                  </button>
                </div>
              </div>
            ) : loading.value === true ? (
              <div class="flex items-center justify-center h-64">
                <span class="loading loading-spinner loading-lg text-primary opacity-60" />
              </div>
            ) : (
              <div class="space-y-5">
                {/* Stats */}
                <div class="grid grid-cols-3 gap-3">
                  <StatCard
                    icon="local_offer"
                    label="Total"
                    value={promotions.value.length}
                    iconBg="bg-primary/10"
                    iconColor="text-primary"
                    valueColor="text-primary"
                  />
                  <StatCard
                    icon="check_circle"
                    label="Active"
                    value={promotions.value.filter((p) => p.enabled).length}
                    iconBg="bg-success/10"
                    iconColor="text-success"
                    valueColor="text-success"
                  />
                  <StatCard
                    icon="cancel"
                    label="Inactive"
                    value={promotions.value.filter((p) => !p.enabled).length}
                    iconBg="bg-base-content/5"
                    iconColor="text-base-content/40"
                    valueColor="text-base-content/50"
                  />
                </div>

                {/* Filters */}
                <div class="flex flex-col sm:flex-row gap-2">
                  <div class="relative flex-1">
                    <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-base">
                      search
                    </span>
                    <input
                      type="text"
                      value={searchQuery.value}
                      onInput={(e) =>
                        (searchQuery.value = (
                          e.target as HTMLInputElement
                        ).value)
                      }
                      placeholder="Search promotions…"
                      class="input input-bordered w-full pl-9 bg-base-200 input-sm"
                    />
                  </div>
                  <select
                    value={filterStatus.value}
                    onChange={(e) =>
                      (filterStatus.value = (e.target as HTMLSelectElement)
                        .value as any)
                    }
                    class="select select-bordered bg-base-200 select-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>

                {/* Table */}
                <ResponsiveTable
                  {...{
                    columns,
                    rows: filtered.value,
                    rowKey: (row: Promotion) => row.id,
                    onRowClick: (row: Promotion) => {
                      editingPromotion.value = row;
                      showEditor.value = true;
                    },
                    loading: false,
                    emptyMessage:
                      searchQuery.value || filterStatus.value !== "all"
                        ? "No promotions match your filters"
                        : "No promotions yet — create your first one",
                  }}
                />
              </div>
            )}
          </AppSubpageContent>

          {showEditor.value && (
            <PromotionEditor
              promotion={editingPromotion.value || undefined}
              onClose={() => {
                showEditor.value = false;
                editingPromotion.value = null;
              }}
              onSave={handleSave}
            />
          )}
        </AppSubpage>
      </div>
    );
  },
});
